import {
  BlockNoteEditor,
  BlockSchema,
  InlineContentSchema,
  insertOrUpdateBlock,
  StyleSchema,
} from '@blocknote/core';
import {
  createReactBlockSpec,
  DefaultReactSuggestionItem,
  ReactCustomBlockRenderProps,
} from '@blocknote/react';
import { getEditorMode } from 'utils/createEditorWithMode';
import { ReactElement } from 'react';
import { useBlockDelete } from 'hooks/useBlockDelete';
import { VideoPlayerIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { TeacherNHVideoBlock } from './teacher/TeacherNHVideoBlock';
import { StudentNHVideoBlock } from './student/StudentNHVideoBlock';
import { ReviewNHVideoBlock } from './reviewer/ReviewNHVideoBlock';
import { BlockBorderBox } from '../common/BlockBorderBox';

export type NHVideoBlockProps = ReactCustomBlockRenderProps<
  (typeof NHVideoBlock)['config'],
  InlineContentSchema,
  StyleSchema
>;

const propSchema = {
  title: {
    default: '',
  },
  description: {
    default: '',
  },
  videoUrl: {
    default: '',
  },
  // TODO(nh-video-deeplink): Store videoId so printed QR codes can deep-link to
  // /app/video/:id and open VideoBox/VideoModal in Novaheal after auth.
  // videoId: {
  //   default: '',
  // },
  thumbnailUrl: {
    default: '',
  },
  qrCodeUrl: {
    default: '',
  },
};

const NHVideoBlockContent = ({ props }: { props: NHVideoBlockProps }): ReactElement => {
  const { editor, block } = props;
  const mode = getEditorMode(editor);

  const { isDeleting, handleDelete } = useBlockDelete({ editor, block, name: 'Video' });

  const handleSeparatorClick = (): void => {
    editor.focus();
    editor.setTextCursorPosition(block, 'end');
    // Simulate Enter key press to create a new block
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    editor.domElement?.dispatchEvent(enterEvent);
    editor.openSuggestionMenu('/');
  };

  const getBlockByMode = (): ReactElement | null => {
    switch (mode) {
      case 'author':
        return <TeacherNHVideoBlock {...props} onDelete={handleDelete} />;
      case 'student':
        return <StudentNHVideoBlock {...props} />;
      case 'review':
        return <ReviewNHVideoBlock {...props} />;
      default:
        return null;
    }
  };

  return (
    <BlockBorderBox
      isDeleting={isDeleting}
      onSeparatorClick={mode === 'author' ? handleSeparatorClick : undefined}
      shouldShowSeparator={mode === 'author'}
      marginTop="l"
      marginBottom="10px"
    >
      {getBlockByMode()}
    </BlockBorderBox>
  );
};

const NHVideoBlock = createReactBlockSpec(
  {
    type: 'nhVideo',
    propSchema,
    content: 'inline',
  },
  {
    render: (props) => <NHVideoBlockContent props={props} />,
  }
);

const createNHVideoBlockSlashItem = <T extends BlockSchema>(
  editor: BlockNoteEditor<T>
): DefaultReactSuggestionItem => ({
  title: 'Novaheal Video',
  onItemClick: () => {
    const insertedBlock = insertOrUpdateBlock(editor, {
      type: 'nhVideo',
    });

    // Add a paragraph block after the inserted block
    editor.insertBlocks(['paragraph'], insertedBlock, 'after');
  },
  aliases: ['nhVideo', 'video'],
  group: 'Interaktiv',
  icon: <NovaIcon icon={VideoPlayerIcon} size="m" color="icon.primary" />,
  subtext: 'Link zu einem Erklärvideo von Novaheal',
});

export { NHVideoBlock, createNHVideoBlockSlashItem };
